<template>
    <AppLayout>
        <div class="container p-4 mx-auto md:px-1 md:py-4">
            <!-- breadcrumb -->
            <nav
                class="mb-6 text-sm font-semibold"
                aria-label="Breadcrumb"
            >
                <ol class="inline-flex p-0 list-none">
                    <li class="flex items-center text-blue-500">
                        <inertia-link
                            :href="route('landing')"
                            class="text-gray-600"
                        >
                            Home
                        </inertia-link>
                        <svg
                            fill="#34D399"
                            class="w-3 h-3 mx-3"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 320 512"
                        >
                            <path
                                d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                            />
                        </svg>
                    </li>
                    <li class="flex items-center">
                        <a
                            href="#"
                            class="text-gray-600"
                            >Salesperson Report</a
                        >
                    </li>
                </ol>
            </nav>
            <!-- breadcrumb end -->

            <div class="flex items-center justify-between p-2">
                <div class="p-0 text-base font-semibold text-gray-900 lg:text-xl dark:text-white">
                    {{ salesperson.name }}
                </div>
                <div class="flex gap-2">
                    <t-datepicker
                        v-model="fromDate"
                        :disabled-dates="disabledFromDates"
                        placeholder="Select start date"
                    >
                        <template
                            slot="day"
                            slot-scope="{ dayFormatted }"
                        >
                            <span>
                                {{ dayFormatted }}
                            </span>
                        </template>
                    </t-datepicker>

                    <t-datepicker
                        v-model="toDate"
                        :disabled-dates="disabledToDates"
                        placeholder="Select end date"
                    >
                        <template
                            slot="day"
                            slot-scope="{ dayFormatted }"
                        >
                            <span>
                                {{ dayFormatted }}
                            </span>
                        </template>
                    </t-datepicker>

                    <t-button
                        :disabled="!fromDate || !toDate"
                        type="button"
                        variant="success"
                        @click="fetchPageData()"
                    >
                        Filter
                    </t-button>
                </div>
            </div>

            <div class="inline-flex my-4">
                <div
                    class="p-4 max-w-lg bg-white rounded-lg border shadow-md sm:p-6 dark:bg-gray-800 dark:border-gray-700"
                >
                    <h5 class="mb-3 text-base font-semibold text-gray-900 lg:text-xl dark:text-white">
                        Sold Amount Report
                    </h5>
                    <p class="text-sm font-normal text-gray-500 dark:text-gray-400">
                        Shows sales amount for present and previous month along with present and previous year.
                    </p>
                    <ul class="my-4 space-y-3">
                        <li>
                            <a
                                href="#"
                                class="flex items-center p-3 text-base font-bold text-gray-900 bg-gray-50 rounded-lg hover:bg-gray-100 group hover:shadow dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                            >
                                <span class="flex-1 ml-3 whitespace-nowrap">Total Sales This Month</span>
                                <span
                                    class="inline-flex items-center justify-center px-2 py-0.5 ml-3 text-xs font-medium text-gray-500 bg-green-200 rounded dark:bg-gray-700 dark:text-gray-400"
                                >
                                    {{ config(common.currency) }} {{ totalSalesThisMonth }}
                                </span>
                            </a>
                        </li>
                        <li>
                            <a
                                href="#"
                                class="flex items-center p-3 text-base font-bold text-gray-900 bg-gray-50 rounded-lg hover:bg-gray-100 group hover:shadow dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                            >
                                <span class="flex-1 ml-3 whitespace-nowrap">Total Sales Last Month</span>
                                <span
                                    class="inline-flex items-center justify-center px-2 py-0.5 ml-3 text-xs font-medium text-gray-500 bg-green-200 rounded dark:bg-gray-700 dark:text-gray-400"
                                >
                                    {{ config(common.currency) }} {{ totalSalesLastMonth }}
                                </span>
                            </a>
                        </li>
                        <li>
                            <a
                                href="#"
                                class="flex items-center p-3 text-base font-bold text-gray-900 bg-gray-50 rounded-lg hover:bg-gray-100 group hover:shadow dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                            >
                                <span class="flex-1 ml-3 whitespace-nowrap">Total Sales This Year</span>
                                <span
                                    class="inline-flex items-center justify-center px-2 py-0.5 ml-3 text-xs font-medium text-gray-500 bg-green-200 rounded dark:bg-gray-700 dark:text-gray-400"
                                >
                                    {{ config(common.currency) }} {{ totalSalesThisYear }}
                                </span>
                            </a>
                        </li>
                        <li>
                            <a
                                href="#"
                                class="flex items-center p-3 text-base font-bold text-gray-900 bg-gray-50 rounded-lg hover:bg-gray-100 group hover:shadow dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                            >
                                <span class="flex-1 ml-3 whitespace-nowrap">Total Sales Last Year</span>
                                <span
                                    class="inline-flex items-center justify-center px-2 py-0.5 ml-3 text-xs font-medium text-gray-500 bg-green-200 rounded dark:bg-gray-700 dark:text-gray-400"
                                >
                                    {{ config(common.currency) }} {{ totalSalesLastYear }}
                                </span>
                            </a>
                        </li>
                        <li>
                            <a
                                href="#"
                                class="flex items-center p-3 text-base font-bold text-gray-900 bg-gray-50 rounded-lg hover:bg-gray-100 group hover:shadow dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                            >
                                <span class="flex-1 ml-3 whitespace-nowrap">Total Sales </span>
                                <span
                                    class="inline-flex items-center justify-center px-2 py-0.5 ml-3 text-xs font-medium text-gray-500 bg-green-200 rounded dark:bg-gray-700 dark:text-gray-400"
                                >
                                    {{ config(common.currency) }} {{ totalSalesSelectedDate() }}
                                </span>
                            </a>
                        </li>
                    </ul>
                    <!-- <div>
                        <a
                            href="#"
                            class="inline-flex items-center text-xs font-normal text-gray-500 hover:underline dark:text-gray-400"
                        >
                            <svg
                                class="mr-2 w-3 h-3"
                                aria-hidden="true"
                                focusable="false"
                                data-prefix="far"
                                data-icon="question-circle"
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            ><path
                                fill="currentColor"
                                d="M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 448c-110.532 0-200-89.431-200-200 0-110.495 89.472-200 200-200 110.491 0 200 89.471 200 200 0 110.53-89.431 200-200 200zm107.244-255.2c0 67.052-72.421 68.084-72.421 92.863V300c0 6.627-5.373 12-12 12h-45.647c-6.627 0-12-5.373-12-12v-8.659c0-35.745 27.1-50.034 47.579-61.516 17.561-9.845 28.324-16.541 28.324-29.579 0-17.246-21.999-28.693-39.784-28.693-23.189 0-33.894 10.977-48.942 29.969-4.057 5.12-11.46 6.071-16.666 2.124l-27.824-21.098c-5.107-3.872-6.251-11.066-2.644-16.363C184.846 131.491 214.94 112 261.794 112c49.071 0 101.45 38.304 101.45 88.8zM298 368c0 23.159-18.841 42-42 42s-42-18.841-42-42 18.841-42 42-42 42 18.841 42 42z"
                            /></svg>
                            Why do I need to connect with my wallet?</a>
                    </div> -->
                </div>

                <div
                    class="mx-4 p-4 max-w-full bg-white rounded-lg border shadow-md sm:p-6 dark:bg-gray-800 dark:border-gray-700"
                >
                    <h5 class="mb-3 text-base font-semibold text-gray-900 lg:text-xl dark:text-white">
                        Highly Sold Products
                    </h5>
                    <apexchart
                        width="500"
                        type="pie"
                        :options="chartOptions"
                        :series="series"
                    />
                </div>
            </div>
            <div class="inline-flex">
                <div
                    class="p-4 max-w-lg bg-white rounded-lg border shadow-md sm:p-6 dark:bg-gray-800 dark:border-gray-700"
                >
                    <div class="flex items-center justify-between">
                        <h5 class="mb-3 text-base font-semibold text-gray-900 lg:text-xl dark:text-white">
                            Commision and Salary
                        </h5>

                        <t-button
                            v-if="pendingCommission > 0"
                            type="button"
                            variant="success"
                            @click="payCommission()"
                        >
                            Pay Commission
                        </t-button>
                    </div>
                    <p class="text-sm font-normal text-gray-500 dark:text-gray-400">
                        Connect with one of our available wallet providers or create a new one.
                    </p>
                    <ul class="my-4 space-y-3">
                        <li>
                            <div
                                class="flex items-center p-3 text-base font-bold text-gray-900 bg-gray-50 rounded-lg hover:bg-gray-100 group hover:shadow dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                            >
                                <span class="flex-1 ml-3 whitespace-nowrap">Basic Pay</span>
                                <span
                                    class="inline-flex items-center justify-center px-2 py-0.5 ml-3 text-xs font-medium text-gray-500 bg-green-200 rounded dark:bg-gray-700 dark:text-gray-400"
                                >
                                    {{ config(common.currency) }} {{ salesperson.sales_person_detail.basic_salary }}
                                </span>
                            </div>
                        </li>
                        <li>
                            <div
                                class="flex items-center p-3 text-base font-bold text-gray-900 bg-gray-50 rounded-lg hover:bg-gray-100 group hover:shadow dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                            >
                                <span class="flex-1 ml-3 whitespace-nowrap">Commision Rate</span>
                                <span
                                    class="inline-flex items-center justify-center px-2 py-0.5 ml-3 text-xs font-medium text-gray-500 bg-green-200 rounded dark:bg-gray-700 dark:text-gray-400"
                                >
                                    {{ salesperson.sales_person_detail.commission }}
                                </span>
                            </div>
                        </li>
                        <li>
                            <div
                                class="flex items-center p-3 text-base font-bold text-gray-900 bg-gray-50 rounded-lg hover:bg-gray-100 group hover:shadow dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                            >
                                <span class="flex-1 ml-3 whitespace-nowrap">Threshold</span>
                                <span
                                    class="inline-flex items-center justify-center px-2 py-0.5 ml-3 text-xs font-medium text-gray-500 bg-green-200 rounded dark:bg-gray-700 dark:text-gray-400"
                                >
                                    {{ config(common.currency) }}
                                    {{ salesperson.sales_person_detail.threshold_sale }}</span
                                >
                            </div>
                        </li>
                        <li>
                            <div
                                class="flex items-center p-3 text-base font-bold text-gray-900 bg-gray-50 rounded-lg hover:bg-gray-100 group hover:shadow dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                            >
                                <span class="flex-1 ml-3 whitespace-nowrap">Commission Reset Date</span>
                                <span
                                    class="inline-flex items-center justify-center px-2 py-0.5 ml-3 text-xs font-medium text-gray-500 bg-green-200 rounded dark:bg-gray-700 dark:text-gray-400"
                                >
                                    {{ latestComission != null ? latestComission.paid_date : 'Not Reset Yet' }}
                                </span>
                            </div>
                        </li>
                        <li>
                            <div
                                class="flex items-center p-3 text-base font-bold text-gray-900 bg-gray-50 rounded-lg hover:bg-gray-100 group hover:shadow dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                            >
                                <span class="flex-1 ml-3 whitespace-nowrap">Total Commission This Period</span>
                                <span
                                    class="inline-flex items-center justify-center px-2 py-0.5 ml-3 text-xs font-medium text-gray-500 bg-green-200 rounded dark:bg-gray-700 dark:text-gray-400"
                                >
                                    {{ config(common.currency) }} {{ pendingCommission }}
                                </span>
                            </div>
                        </li>
                        <li>
                            <div
                                class="flex items-center p-3 text-base font-bold text-gray-900 bg-gray-50 rounded-lg hover:bg-gray-100 group hover:shadow dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                            >
                                <span class="flex-1 ml-3 whitespace-nowrap">Total Commission Collected This Year</span>
                                <span
                                    class="inline-flex items-center justify-center px-2 py-0.5 ml-3 text-xs font-medium text-gray-500 bg-green-200 rounded dark:bg-gray-700 dark:text-gray-400"
                                >
                                    {{ config(common.currency) }}
                                    {{ (totalSalesThisYear * salesperson.sales_person_detail.commission) / 100 }}
                                </span>
                            </div>
                        </li>
                    </ul>
                    <!-- <div>
                        <a
                            href="#"
                            class="inline-flex items-center text-xs font-normal text-gray-500 hover:underline dark:text-gray-400"
                        >
                            <svg
                                class="mr-2 w-3 h-3"
                                aria-hidden="true"
                                focusable="false"
                                data-prefix="far"
                                data-icon="question-circle"
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            ><path
                                fill="currentColor"
                                d="M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 448c-110.532 0-200-89.431-200-200 0-110.495 89.472-200 200-200 110.491 0 200 89.471 200 200 0 110.53-89.431 200-200 200zm107.244-255.2c0 67.052-72.421 68.084-72.421 92.863V300c0 6.627-5.373 12-12 12h-45.647c-6.627 0-12-5.373-12-12v-8.659c0-35.745 27.1-50.034 47.579-61.516 17.561-9.845 28.324-16.541 28.324-29.579 0-17.246-21.999-28.693-39.784-28.693-23.189 0-33.894 10.977-48.942 29.969-4.057 5.12-11.46 6.071-16.666 2.124l-27.824-21.098c-5.107-3.872-6.251-11.066-2.644-16.363C184.846 131.491 214.94 112 261.794 112c49.071 0 101.45 38.304 101.45 88.8zM298 368c0 23.159-18.841 42-42 42s-42-18.841-42-42 18.841-42 42-42 42 18.841 42 42z"
                            /></svg>
                            Why do I need to connect with my wallet?</a>
                    </div> -->
                </div>

                <div
                    class="mx-4 p-4 max-w-full bg-white rounded-lg border shadow-md sm:p-6 dark:bg-gray-800 dark:border-gray-700"
                >
                    <h5 class="mb-3 text-base font-semibold text-gray-900 lg:text-xl dark:text-white">
                        Sales Current Year vs Last Year
                    </h5>
                    <apexchart
                        width="500"
                        type="bar"
                        :options="barChartOptions"
                        :series="barSeries"
                    />
                </div>
            </div>

            <div
                class="my-5 p-4 max-w-full bg-white rounded-lg border shadow-md sm:p-6 dark:bg-gray-800 dark:border-gray-700"
            >
                <h5 class="mb-3 text-base font-semibold text-gray-900 lg:text-xl dark:text-white">Retailer List</h5>
                <p class="text-sm font-normal text-gray-500 dark:text-gray-400">
                    List of all retailer in Purchasing Popularity
                </p>
                <t-table
                    :responsive="true"
                    :responsive-breakpoint="640"
                    :headers="['RETAILER NAME', 'EMAIL', 'TOTAL SALES']"
                    class="my-4"
                >
                    <template
                        slot="tbody"
                        slot-scope="props"
                    >
                        <tr
                            v-for="invoice in bills"
                            :key="'invoice-' + invoice.id"
                            :class="[props.trClass]"
                        >
                            <!-- Retailer Name -->
                            <td :class="props.tdClass">
                                {{ invoice.name }}
                            </td>
                            <td :class="props.tdClass">
                                {{ invoice.email }}
                            </td>
                            <td :class="[props.tdClass, 'text-green-500']">
                                {{ config(common.currency) }} {{ sumAmountFromTransactions(invoice.bills) }}
                            </td>
                        </tr>
                    </template>
                </t-table>
            </div>
        </div>
    </AppLayout>
</template>

<script>
    import VueApexCharts from 'vue-apexcharts';
    import moment from 'moment';
    import axios from 'axios';
    import AppLayout from '../../Layouts/AppLayout.vue';
    import common from '../../common';

    export default {
        name: 'SalesPersonReport',

        components: {
            AppLayout,
            apexchart: VueApexCharts,
        },

        props: {
            bills: {
                type: Array,
                required: true,
            },
            salesperson: {
                type: Object,
                required: true,
            },
            totalSalesThisMonth: {
                type: Number,
                required: true,
            },
            totalSalesLastMonth: {
                type: Number,
                required: true,
            },
            totalSalesThisYear: {
                type: Number,
                required: true,
            },
            totalSalesLastYear: {
                type: Number,
                required: true,
            },
            totalMonthwiseSales: {
                type: Array,
                required: true,
            },
            totalYearwiseSales: {
                type: Array,
                required: true,
            },
            appliedFilters: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                common,
                fromDate: null,
                toDate: null,
                allPaidComissions: [],
                latestComission: null,
                pendingCommission: 0,
                totalAmount: 0,
                totalQuantity: 0,
                series: this.totalMonthwiseSales.map((value) => value.totalQty),
                chartOptions: {
                    chart: {
                        width: 380,
                        type: 'pie',
                    },
                    labels: this.totalMonthwiseSales.map((value) => value.productName),
                    responsive: [
                        {
                            breakpoint: 480,
                            options: {
                                chart: {
                                    width: 200,
                                },
                                legend: {
                                    position: 'bottom',
                                },
                            },
                        },
                    ],
                },

                barChartOptions: {
                    chart: {
                        id: 'yearwisechart',
                        type: 'bar',
                    },
                    plotOptions: {
                        bar: {
                            horizontal: false,
                            columnWidth: '55%',
                            endingShape: 'rounded',
                        },
                    },
                    dataLabels: {
                        enabled: false,
                    },
                    stroke: {
                        show: true,
                        width: 2,
                        colors: ['transparent'],
                    },
                    xaxis: {
                        categories: this.totalYearwiseSales.map((value) => value.year),
                    },
                    yaxis: {
                        title: {
                            text: `Count / ${this.config(common.currency)}`,
                        },
                    },
                    fill: {
                        opacity: 1,
                    },
                    tooltip: {
                        y: {
                            formatter(val) {
                                return `$ ${val}`;
                            },
                        },
                    },
                },
                barSeries: [
                    {
                        name: 'Total Qty',
                        data: this.totalYearwiseSales.map((value) => value.totalQty),
                    },
                    {
                        name: 'Total Amt',
                        data: this.totalYearwiseSales.map((value) => value.grandTotal),
                    },
                ],
            };
        },

        created() {
            this.fetchCommissionRecord();
        },

        mounted() {
            if (this.appliedFilters.fromDate) {
                this.fromDate = this.appliedFilters.fromDate;
            }

            if (this.appliedFilters.toDate) {
                this.toDate = this.appliedFilters.toDate;
            }
        },

        methods: {
            disabledFromDates(date) {
                return moment(date).format('YYYY-MM-DD') > moment().format('YYYY-MM-DD');
            },

            disabledToDates(date) {
                console.log(moment(date).format('YYYY-MM-DD'));
                console.log(moment(this.fromDate).format('YYYY-MM-DD'));
                return this.fromDate != null
                    ? moment(date).format('YYYY-MM-DD') <= moment(this.fromDate).format('YYYY-MM-DD') ||
                          moment(date).format('YYYY-MM-DD') > moment().format('YYYY-MM-DD')
                    : moment(date).format('YYYY-MM-DD') > moment().format('YYYY-MM-DD');
            },

            sumAmountFromTransactions(transactions) {
                let sumGrandTotal = 0;
                transactions.forEach((transaction) => {
                    sumGrandTotal += parseFloat(transaction.total_amount);
                });
                return sumGrandTotal.toFixed(2);
            },

            totalSalesSelectedDate() {
                return this.totalYearwiseSales
                    .map((transaction) => transaction.grandTotal)
                    .reduce((previousValue, currentValue) => parseInt(previousValue) + parseInt(currentValue), 0)
                    .toFixed(2);
            },

            fetchCommissionRecord() {
                axios.get(route('commission.index', { salesPersonId: this.salesperson.id })).then((res) => {
                    this.allPaidComissions = res.data.allPaidComissions;
                    this.latestComission = res.data.latestComission;
                    this.totalAmount = res.data.totalAmount;
                    this.totalQuantity = res.data.totalQuantity;
                    this.pendingCommission =
                        (res.data.totalAmount * this.salesperson.sales_person_detail.commission) / 100;
                });
            },

            fetchPageData() {
                this.$inertia.get(
                    route('reports.salesperson.orders', {
                        salesperson: this.salesperson.id,
                        from: this.fromDate,
                        to: this.toDate,
                    }),
                );
            },

            payCommission() {
                // const brand = this.brands.data.find((item) => item.id === brandId);
                this.$dialog
                    .confirm({
                        title: 'Confirm',
                        text: `Are you sure you want to proceed with the commission with amount ${this.config(
                            common.currency,
                        )} ${this.pendingCommission}?`,
                    })
                    .then((result) => {
                        if (result.isOk) {
                            axios
                                .post(route('commission.store'), {
                                    salesPersonId: this.salesperson.id,
                                    paidDate: moment().format('YYYY-MM-DD'),
                                    paidAmount: this.pendingCommission,
                                })
                                .then(() => this.fetchCommissionRecord());
                        }
                    });
            },
        },
    };
</script>
